import * as d3 from "d3";
import React, { useState } from "react";
import { Box, Button, Grid, Paper, Stack, Typography } from "@mui/material";
import { PageLayout } from "../components/PageLayout";
import { useMatchOptions } from "../components/simple-select/simpleSelectOptions";
import { SingleSelect } from "../components/simple-select/SimpleSelect";
import { CSVLink } from "react-csv";
import { MetricEvent, useMatchMetricEvents } from "../requests/metricEvents";

import { CustomSpinner } from "../components/CustomSpinner";
import { useAwayTeam, useHomeTeam } from "../requests/teams";
import { StatItem } from "../components/Analytics/StatItem";
import { borderRadius, shadows } from "../utils/designSystem";
import { EChartsBarComparison } from "../components/Analytics/EChartsBarComparison";
import { PitchExplorer } from "../components/Analytics/PitchExplorer";
import { ANALYTICS_DESCRIPTION } from "./Home";

type Group = {
  group: string;
  MetersGained: number;
  TimeOfPossession: string; // Change the type to string
  territoryPct: string;
  tackles: number;
  penalties: number;
  points: number;
  home: string;
  away: string;
  color: string;
};

const transformEventsToStats = (filteredTableEvents: MetricEvent[]): StatItem[] => {
  const getAggregatedMetrics = (
    teamName: string,
    metricEvents: MetricEvent[]
  ): Group | undefined => {
    if (metricEvents.length === 0) {
      return undefined;
    }

    const territorySeconds = d3.sum(metricEvents, (d) => d.seconds_elapsed * d.territory);
    const possessionSeconds = d3.sum(metricEvents, (d) => d.seconds_elapsed * d.possession);
    const totalSeconds = d3.sum(metricEvents, (d) => d.seconds_elapsed);
    const totalTerrSeconds = d3.sum(
      metricEvents?.filter((event) => event.x !== 50),
      (d) => d.seconds_elapsed
    );
    return {
      group: teamName,
      MetersGained: Math.max(
        0,
        d3.sum(metricEvents, (d) => d.meters_gained)
      ),
      TimeOfPossession: `${((100 * possessionSeconds) / totalSeconds).toFixed(1)}%`,
      territoryPct: `${((100 * territorySeconds) / totalTerrSeconds).toFixed(1)}%`,
      tackles: d3.sum(metricEvents, (d) => d.tackles),
      penalties: d3.sum(metricEvents, (d) => d.penalties),
      points: d3.sum(metricEvents, (d) => d.points),
      home: d3.max(metricEvents, (d) => d.home_team) ?? "Home Team",
      away: d3.max(metricEvents, (d) => d.away_team) ?? "Away Team",
      color: d3.max(metricEvents, (d) => d.color) ?? "",
    };
  };

  const teamMetricEvents = d3.group(filteredTableEvents, (d) => d.team);

  const homeTeamName = filteredTableEvents?.[0]?.home_team;
  const homeTeamAggregatedMetrics = getAggregatedMetrics(
    homeTeamName,
    teamMetricEvents.get(homeTeamName) || []
  );

  const awayTeamName = filteredTableEvents?.[0]?.away_team;
  const awayTeamAggregatedMetrics = getAggregatedMetrics(
    awayTeamName,
    teamMetricEvents.get(awayTeamName) || []
  );

  const fields: { key: keyof Group; headerName: string }[] = [
    { key: "points", headerName: "Points" },
    { key: "MetersGained", headerName: "Meters Gained" },
    { key: "TimeOfPossession", headerName: "Possession %" },
    { key: "territoryPct", headerName: "Territory %" },
    { key: "tackles", headerName: "Tackles" },
    { key: "penalties", headerName: "Penalties" },
    { key: "color", headerName: "Color" },
    { key: "group", headerName: "TeamName" },
  ];

  return fields.map((field) => ({
    metric: field.headerName,
    home: String(homeTeamAggregatedMetrics?.[field.key]) ?? "",
    away: String(awayTeamAggregatedMetrics?.[field.key]) ?? "",
  }));
};

export const Analytics = () => {
  const [selectedFilterMatchId, setSelectedFilterMatchId] = useState("");

  const selectedFilterMatchIdInt = selectedFilterMatchId.length
    ? parseInt(selectedFilterMatchId)
    : undefined;

  const matchSelected = selectedFilterMatchId !== "";
  const homeTeam = useHomeTeam(selectedFilterMatchIdInt);
  const awayTeam = useAwayTeam(selectedFilterMatchIdInt);

  const metricEvents = useMatchMetricEvents(selectedFilterMatchIdInt);
  const matchOptions = useMatchOptions();

  React.useEffect(() => {
    if (metricEvents?.length) {
      console.log("MetricEvent sample:", metricEvents[0]);
    }
  }, [metricEvents]);

  const [fileName, setFileName] = useState("");

  const getFilteredTableEvents = (
    filterMatchId?: number,
    filterHalfNumbers?: number[]
  ): MetricEvent[] => {
    let filteredTableEvents = filterMatchId
      ? metricEvents?.filter((event) => event.match_id === filterMatchId)
      : metricEvents;
    if (filterHalfNumbers && filterHalfNumbers.length > 0) {
      filteredTableEvents = filteredTableEvents?.filter((event) =>
        filterHalfNumbers.includes(event.half_number)
      );
    }

    return filteredTableEvents ?? [];
  };

  if (!matchOptions) {
    return <CustomSpinner />;
  }

  // Calculate totals with the selected filterMatchId and selectedHalfNumbers
  const filteredTableEvents = getFilteredTableEvents(selectedFilterMatchIdInt);
  const stats = transformEventsToStats(filteredTableEvents);

  const filteredStats = stats
    .filter((row) => row.metric !== "TeamName")
    .filter((row) => row.metric !== "Color");

  return (
    <PageLayout
      title="Analytics"
      subtitle={ANALYTICS_DESCRIPTION}
      maxWidth="xl"
      showPaper={false}
      headerActions={
        filteredTableEvents && (
          <CSVLink data={filteredTableEvents} filename={fileName}>
            <Button variant="contained" size="small">
              Download CSV
            </Button>
          </CSVLink>
        )
      }
    >
      <Stack>
        <SingleSelect
          label="Select a Match to View Stats and Analytics"
          widthFactor={3}
          value={String(selectedFilterMatchId)}
          setValue={(value) => {
            const matchOption = matchOptions?.find((matchOption) => matchOption.id === value);
            setSelectedFilterMatchId(value);
            setFileName(matchOption?.value || "");
          }}
          options={matchOptions || []}
          none
        />
      </Stack>
      {metricEvents === undefined ? (
        <CustomSpinner message="Analyzing your match...this may take up to thirty seconds." />
      ) : (
        matchSelected &&
        homeTeam &&
        awayTeam && (
          <>
            {/* Charts */}
            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} md={5}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: borderRadius.lg,
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow: shadows.sm,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    Match Stats Comparison
                  </Typography>
                  <Box sx={{ flex: 1, minHeight: 320 }}>
                    <EChartsBarComparison
                      stats={filteredStats}
                      homeTeam={homeTeam}
                      awayTeam={awayTeam}
                    />
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={7}>
                <PitchExplorer
                  metricEvents={metricEvents}
                  selectedMatchId={selectedFilterMatchIdInt}
                />
              </Grid>
            </Grid>
          </>
        )
      )}
    </PageLayout>
  );
};
